/**
 * Billing presentation logic, kept out of the component.
 *
 * WHY a pure module: what a seller may do next is decided entirely by the subscription's status,
 * and getting that wrong is expensive in both directions — offering "Subscribe" on an ACTIVE
 * charge would double-bill, hiding it on an EXPIRED one leaves the store unable to pay us. These
 * are exactly the rules worth unit-testing, and the vitest environment here is `node`, so they
 * cannot live inside JSX.
 *
 * The vocabulary is Shopify's `AppSubscriptionStatus` plus our UNKNOWN sentinel, mirroring
 * SUBSCRIPTION_STATUSES in packages/core/src/domain — a status we do not recognise is never read
 * as "paying".
 */
import { daysUntil } from "./format";

/** Statuses that mean the store is entitled to the app. Mirrors ENTITLING_SUBSCRIPTION_STATUSES. */
export const ENTITLING_STATUSES: ReadonlySet<string> = new Set(["ACTIVE", "ACCEPTED"]);

export type BillingTone = "good" | "warn" | "bad" | "info" | "neutral";

/** The subset of a subscription this module reasons about — both API shapes satisfy it. */
export interface SubscriptionLike {
  status: string;
  trial_ends_at: string | null;
  current_period_end: string | null;
  is_test: boolean;
  confirmation_url?: string | null;
}

export interface BillingView {
  tone: BillingTone;
  headline: string;
  /** One sentence a seller can act on — what this status means and what happens next. */
  detail: string;
  entitled: boolean;
  /** Create a charge. Never true while one is entitling: that would bill the store twice. */
  canSubscribe: boolean;
  canCancel: boolean;
  /** Shopify's approval page for a charge still waiting to be accepted. */
  approvalUrl: string | null;
}

const NOT_SUBSCRIBED: BillingView = {
  tone: "neutral",
  headline: "Not subscribed",
  detail: "No charge has been created for this store yet. Subscribing sends you to Shopify to approve it.",
  entitled: false,
  canSubscribe: true,
  canCancel: false,
  approvalUrl: null,
};

/** Whole days left in the trial; null when there is no trial or it has already ended. */
export function trialDaysLeft(sub: SubscriptionLike | null, now: Date = new Date()): number | null {
  const days = daysUntil(sub?.trial_ends_at, now);
  return days === null || days < 0 ? null : days;
}

export function isEntitled(sub: SubscriptionLike | null): boolean {
  return sub !== null && ENTITLING_STATUSES.has(sub.status);
}

/**
 * Everything the billing screen renders and every action it offers, from one status.
 *
 * PENDING is the subtle case: a charge exists, so a second one must not be created — unless we
 * have no approval URL to send the merchant to, in which case a fresh charge is the only way out
 * of a dead end.
 */
export function billingView(sub: SubscriptionLike | null, now: Date = new Date()): BillingView {
  if (!sub) return NOT_SUBSCRIBED;
  const approvalUrl = sub.confirmation_url ?? null;

  switch (sub.status) {
    case "ACTIVE": {
      const left = trialDaysLeft(sub, now);
      return {
        tone: "good",
        headline: left === null ? "Active" : "Active — free trial",
        detail: left === null
          ? "Shopify bills this store on your app subscription and charges it to the store's Shopify invoice."
          : `Free trial: ${left === 0 ? "the last day" : `${left} ${left === 1 ? "day" : "days"} left`}. Nothing is charged until it ends.`,
        entitled: true, canSubscribe: false, canCancel: true, approvalUrl: null,
      };
    }
    case "ACCEPTED":
      return {
        tone: "good",
        headline: "Approved",
        detail: "You approved the charge. Shopify has not moved it to ACTIVE yet — that usually takes a moment.",
        entitled: true, canSubscribe: false, canCancel: true, approvalUrl: null,
      };
    case "PENDING":
      return {
        tone: "info",
        headline: "Waiting for your approval",
        detail: approvalUrl
          ? "The charge exists but nobody has approved it in Shopify Admin. Shopify expires an unapproved charge after two days."
          : "The charge is waiting for approval, but we no longer hold its approval link. Create a new charge to get a fresh one.",
        entitled: false, canSubscribe: approvalUrl === null, canCancel: false, approvalUrl,
      };
    case "DECLINED":
      return {
        tone: "bad",
        headline: "Declined",
        detail: "The charge was declined in Shopify Admin. You can create a new one at any time.",
        entitled: false, canSubscribe: true, canCancel: false, approvalUrl: null,
      };
    case "EXPIRED":
      return {
        tone: "warn",
        headline: "Expired",
        detail: "The approval request expired before it was accepted. Create a new charge to subscribe.",
        entitled: false, canSubscribe: true, canCancel: false, approvalUrl: null,
      };
    case "FROZEN":
      return {
        tone: "warn",
        headline: "Frozen by Shopify",
        detail: "Shopify has frozen this shop's charges — normally an unpaid Shopify invoice. The subscription resumes when the shop does; a new charge would not help.",
        entitled: false, canSubscribe: false, canCancel: false, approvalUrl: null,
      };
    case "CANCELLED":
      return {
        tone: "neutral",
        headline: "Cancelled",
        detail: "This subscription was cancelled. Subscribing again creates a new charge for you to approve.",
        entitled: false, canSubscribe: true, canCancel: false, approvalUrl: null,
      };
    default:
      return {
        tone: "warn",
        headline: "Unrecognised status",
        detail: `Shopify reported "${sub.status}", which this version of PayoutAudit does not know. Refreshing from Shopify is safe; creating a charge is not offered until the state is clear.`,
        entitled: false, canSubscribe: false, canCancel: false, approvalUrl,
      };
  }
}

/** Compact state for a store card in the picker, where there is room for a few words only. */
export function shortPlanLabel(sub: SubscriptionLike | null, now: Date = new Date()): string {
  if (!sub) return "Not subscribed";
  if (sub.status === "ACTIVE") {
    const left = trialDaysLeft(sub, now);
    return left === null ? "Active" : `Trial — ${left === 0 ? "last day" : `${left}d left`}`;
  }
  return billingView(sub, now).headline;
}

/** Billing intervals, spelled the way the offer reads in a sentence. */
const INTERVAL_LABELS: Readonly<Record<string, string>> = {
  EVERY_30_DAYS: "every 30 days",
  ANNUAL: "a year",
};

export function intervalLabel(interval: string): string {
  return INTERVAL_LABELS[interval] ?? interval.toLowerCase().replace(/_/g, " ");
}

export interface ReturnNotice {
  tone: BillingTone;
  title: string;
  text: string;
}

/**
 * What to tell the merchant who has just come back from Shopify's approval page.
 *
 * The value is the status our API read back FROM Shopify after the redirect (lower-cased), plus
 * `none` when no charge was found and `error` when the re-query itself failed. It is not the
 * browser's claim about what happened, so it can be shown as fact — except `error`, which says
 * only that we do not know.
 */
export function returnNotice(param: string | null): ReturnNotice | null {
  switch (param) {
    case "active":
    case "accepted":
      return { tone: "good", title: "Subscription confirmed", text: "Shopify confirmed your approval and this store is now on the plan." };
    case "pending":
      return { tone: "info", title: "Not approved yet", text: "We re-read the charge from Shopify and it is still waiting for approval." };
    case "declined":
      return { tone: "warn", title: "Charge declined", text: "The charge was declined in Shopify. Nothing has been billed." };
    case "expired":
      return { tone: "warn", title: "Charge expired", text: "The approval request expired before it was accepted." };
    case "cancelled":
      return { tone: "neutral", title: "Subscription cancelled", text: "Shopify reports this subscription as cancelled." };
    case "frozen":
      return { tone: "warn", title: "Charges frozen", text: "Shopify has frozen this shop's charges. The subscription resumes when the shop does." };
    case "none":
      return { tone: "warn", title: "No charge found", text: "Shopify has no app subscription for this store. Nothing was created." };
    case "error":
      return { tone: "bad", title: "Could not confirm with Shopify", text: "You may well have approved the charge — we simply could not read it back. Refresh from Shopify to check." };
    case "unknown":
      return { tone: "warn", title: "Unrecognised state", text: "Shopify answered with a status this version does not know. Refresh from Shopify to see the raw value." };
    default:
      return null;
  }
}
